/**
 * CreateMainGoalScreen
 * Screen for creating the main goal during onboarding
 */

import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  SafeAreaView,
  KeyboardAvoidingView,
  Platform,
  ActivityIndicator,
  Alert,
} from 'react-native';
import { CreateMainGoalScreenProps } from '../navigation/types';
import { useGoals } from '../hooks/useGoals';

export const CreateMainGoalScreen: React.FC<CreateMainGoalScreenProps> = ({ navigation, route }) => {
  const { createGoalSafe, currentAccountId } = useGoals();
  const [goalName, setGoalName] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  // Check if we're creating a side goal or main goal
  const goalType = (route.params as any)?.type || 'main';

  // Use account ID from params if provided, otherwise use context
  const accountId = (route.params as any)?.accountId || currentAccountId;

  const handleCreate = async () => {
    // Validate
    const trimmedName = goalName.trim();
    if (trimmedName.length === 0) {
      if (Platform.OS === 'web') {
        window.alert('Please enter a goal name');
      } else {
        Alert.alert('Error', 'Please enter a goal name');
      }
      return;
    }

    try {
      setIsCreating(true);

      // Create goal with the specified type
      await createGoalSafe(trimmedName, goalType, accountId);

      // Navigate based on goal type
      if (goalType === 'side') {
        // For daily actions on web, just go back (Alert.alert doesn't work well on web)
        if (Platform.OS === 'web') {
          navigation.goBack();
        } else {
          // For daily actions on mobile, ask if they want to add another
          Alert.alert(
            'Daily Action Added!',
            `"${trimmedName}" has been added. Would you like to add another daily action?`,
            [
              {
                text: 'Add Another',
                onPress: () => {
                  // Reset the form and stay on this screen
                  setGoalName('');
                  setIsCreating(false);
                },
              },
              {
                text: 'Done',
                onPress: () => {
                  navigation.goBack();
                },
                style: 'cancel',
              },
            ]
          );
        }
      } else {
        navigation.replace('Today');
      }
    } catch (error) {
      console.error('Error creating goal:', error);
      if (Platform.OS === 'web') {
        window.alert(error instanceof Error ? error.message : 'Failed to create goal');
      } else {
        Alert.alert('Error', error instanceof Error ? error.message : 'Failed to create goal');
      }
      setIsCreating(false);
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <KeyboardAvoidingView
        style={styles.keyboardView}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      >
        <View style={styles.content}>
          {/* Header */}
          <View style={styles.header}>
            <Text style={styles.icon}>{goalType === 'side' ? '⭐' : '🎯'}</Text>
            <Text style={styles.title}>
              {goalType === 'side' ? 'Add a Daily Action' : 'Create Your Main Goal'}
            </Text>
            <Text style={styles.subtitle}>
              {goalType === 'side'
                ? "Daily actions are the specific tasks that support your main goal. Like exercising, running, or yoga to support 'Be Active'."
                : "Choose your main focus area - the broad theme you want to build your life around. You'll add specific daily actions next."}
            </Text>
          </View>

          {/* Input */}
          <View style={styles.inputContainer}>
            <Text style={styles.label}>{goalType === 'side' ? 'Daily Action' : 'Main Goal'}</Text>
            <TextInput
              style={styles.input}
              placeholder={goalType === 'side' ? 'e.g., Exercise for 30 minutes' : 'e.g., Be Active'}
              placeholderTextColor="#999"
              value={goalName}
              onChangeText={setGoalName}
              autoFocus
              autoCapitalize="sentences"
              returnKeyType="done"
              onSubmitEditing={handleCreate}
              editable={!isCreating}
              multiline
              numberOfLines={2}
            />
          </View>

          {/* Examples with categorized suggestions */}
          <View style={styles.suggestions}>
            <Text style={styles.suggestionsTitle}>
              {goalType === 'side' ? 'Common Daily Actions:' : 'Choose Your Main Focus:'}
            </Text>
            {goalType === 'side' ? (
              <View style={styles.suggestionChips}>
                <TouchableOpacity style={styles.chip} onPress={() => setGoalName('Exercise for 30 minutes')}>
                  <Text style={styles.chipText}>🏃 Exercise 30min</Text>
                </TouchableOpacity>
                <TouchableOpacity style={styles.chip} onPress={() => setGoalName('Go for a run')}>
                  <Text style={styles.chipText}>🏃 Go running</Text>
                </TouchableOpacity>
                <TouchableOpacity style={styles.chip} onPress={() => setGoalName('Practice yoga')}>
                  <Text style={styles.chipText}>🧘 Yoga</Text>
                </TouchableOpacity>
                <TouchableOpacity style={styles.chip} onPress={() => setGoalName('Meditate for 10 minutes')}>
                  <Text style={styles.chipText}>🧘 Meditate 10min</Text>
                </TouchableOpacity>
                <TouchableOpacity style={styles.chip} onPress={() => setGoalName('Read for 30 minutes')}>
                  <Text style={styles.chipText}>📚 Read 30min</Text>
                </TouchableOpacity>
                <TouchableOpacity style={styles.chip} onPress={() => setGoalName('Write 500 words')}>
                  <Text style={styles.chipText}>✍️ Write 500 words</Text>
                </TouchableOpacity>
                <TouchableOpacity style={styles.chip} onPress={() => setGoalName('Practice gratitude')}>
                  <Text style={styles.chipText}>🙏 Gratitude</Text>
                </TouchableOpacity>
                <TouchableOpacity style={styles.chip} onPress={() => setGoalName('Drink 8 glasses of water')}>
                  <Text style={styles.chipText}>💧 Hydrate</Text>
                </TouchableOpacity>
              </View>
            ) : (
              <View style={styles.suggestionChips}>
                <TouchableOpacity style={styles.chip} onPress={() => setGoalName('Be Active')}>
                  <Text style={styles.chipText}>🏃 Be Active</Text>
                </TouchableOpacity>
                <TouchableOpacity style={styles.chip} onPress={() => setGoalName('Be Mindful')}>
                  <Text style={styles.chipText}>🧘 Be Mindful</Text>
                </TouchableOpacity>
                <TouchableOpacity style={styles.chip} onPress={() => setGoalName('Improve Self')}>
                  <Text style={styles.chipText}>📚 Improve Self</Text>
                </TouchableOpacity>
                <TouchableOpacity style={styles.chip} onPress={() => setGoalName('Improve Health')}>
                  <Text style={styles.chipText}>❤️ Improve Health</Text>
                </TouchableOpacity>
                <TouchableOpacity style={styles.chip} onPress={() => setGoalName('Build Relationships')}>
                  <Text style={styles.chipText}>👥 Build Relationships</Text>
                </TouchableOpacity>
                <TouchableOpacity style={styles.chip} onPress={() => setGoalName('Create & Build')}>
                  <Text style={styles.chipText}>🛠️ Create & Build</Text>
                </TouchableOpacity>
                <TouchableOpacity style={styles.chip} onPress={() => setGoalName('Practice Discipline')}>
                  <Text style={styles.chipText}>💪 Practice Discipline</Text>
                </TouchableOpacity>
                <TouchableOpacity style={styles.chip} onPress={() => setGoalName('Find Balance')}>
                  <Text style={styles.chipText}>⚖️ Find Balance</Text>
                </TouchableOpacity>
              </View>
            )}
          </View>

          {/* Frequency Note */}
          <View style={styles.frequencyNote}>
            <Text style={styles.frequencyNoteText}>
              {goalType === 'side'
                ? "Track this action as often as you like - daily, weekly, whenever works for you."
                : "Your main goal is the theme. You'll track specific daily actions that support it."}
            </Text>
          </View>

          {/* CTA */}
          <TouchableOpacity
            style={[styles.createButton, isCreating && styles.createButtonDisabled]}
            onPress={handleCreate}
            disabled={isCreating}
          >
            {isCreating ? (
              <ActivityIndicator color="#fff" />
            ) : (
              <Text style={styles.createButtonText}>
                {goalType === 'side' ? 'Add Daily Action' : 'Create Goal & Start'}
              </Text>
            )}
          </TouchableOpacity>

          {/* Helper text */}
          {goalType === 'main' && (
            <Text style={styles.helperText}>
              Next, you'll add specific daily actions that support this main focus.
            </Text>
          )}
        </View>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#fff',
  },
  keyboardView: {
    flex: 1,
  },
  content: {
    flex: 1,
    padding: 24,
    justifyContent: 'center',
  },
  header: {
    marginBottom: 32,
    alignItems: 'center',
  },
  icon: {
    fontSize: 64,
    marginBottom: 16,
  },
  title: {
    fontSize: 28,
    fontWeight: '700',
    color: '#333',
    marginBottom: 12,
    textAlign: 'center',
  },
  subtitle: {
    fontSize: 16,
    color: '#666',
    lineHeight: 24,
    textAlign: 'center',
  },
  inputContainer: {
    marginBottom: 24,
  },
  label: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginBottom: 8,
  },
  input: {
    backgroundColor: '#f5f5f5',
    borderRadius: 8,
    padding: 16,
    fontSize: 16,
    color: '#333',
    borderWidth: 1,
    borderColor: '#e0e0e0',
    minHeight: 60,
    textAlignVertical: 'top',
  },
  suggestions: {
    marginBottom: 24,
  },
  suggestionsTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginBottom: 16,
  },
  categorySection: {
    marginBottom: 20,
  },
  categoryTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#666',
    marginBottom: 10,
  },
  suggestionChips: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  chip: {
    backgroundColor: '#F0F8FF',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: '#007AFF',
    marginRight: 8,
    marginBottom: 8,
  },
  chipText: {
    fontSize: 14,
    color: '#007AFF',
    fontWeight: '500',
  },
  createButton: {
    backgroundColor: '#007AFF',
    paddingVertical: 16,
    borderRadius: 8,
    alignItems: 'center',
    marginBottom: 16,
  },
  createButtonDisabled: {
    opacity: 0.6,
  },
  createButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#fff',
  },
  helperText: {
    fontSize: 14,
    color: '#999',
    textAlign: 'center',
    lineHeight: 20,
  },
  frequencyNote: {
    marginBottom: 16,
    backgroundColor: '#FFF9E6',
    padding: 12,
    borderRadius: 8,
    borderLeftWidth: 3,
    borderLeftColor: '#FFB800',
  },
  frequencyNoteText: {
    fontSize: 13,
    color: '#666',
    lineHeight: 18,
    fontStyle: 'italic',
  },
});
